import React, { useState } from "react";
import Layout from "../components/Layout";
import { QuestionMarkCircleIcon, ChevronDownIcon } from "@heroicons/react/24/solid";

const faqs = [
  {
    category: "Booking & Seats",
    items: [
      { q: "Is this a shared shuttle or private charter?", a: "Shared, per-seat shuttle by default. You pay only for the seats you need. Private charters are available on request." },
      { q: "Which routes do you serve?", a: "Okanagan & Interior BC only: Kelowna (YLW) ↔ Big White, Vernon ↔ SilverStar, Kamloops ↔ Sun Peaks, Penticton ↔ Apex Mountain, and select Revelstoke runs (seasonal/availability)." },
      { q: "How do I choose the number of seats?", a: "Select your passenger count during booking. If your group size changes later, contact us and we’ll help adjust if seats are available." },
      { q: "Do you offer child seats?", a: "Yes. Request them during booking (limited availability). Please include the child’s age/weight so we bring the right seat." },
    ],
  },
  {
    category: "Pickups, Timing & Operations",
    items: [
      { q: "Where are the pickup points?", a: "Exact pickup points are shown during booking and in your confirmation email. Common points include airports (e.g., YLW) and central city locations near resort shuttles. Arrive 10 minutes early." },
      { q: "What if my flight is delayed?", a: "On airport routes we monitor arrivals where possible. If you’re delayed, we’ll attempt to move you to the next available departure. Call us day-of-travel for fastest help." },
      { q: "What if I’m running late?", a: "Call us immediately. We’ll advise options based on the day’s departures. Missed departures may require rebooking if seats are available." },
    ],
  },
  {
    category: "Luggage, Skis & Boards",
    items: [
      { q: "How much gear can I bring?", a: "One standard suitcase or duffel + one ski/board bag per person is typical. If you have extra or oversized items, tell us before travel so we can confirm space." },
      { q: "Is there a fee for ski/board bags?", a: "Ski/board bags are included for typical loads. Oversize or extra items may require a surcharge or a different departure." },
      { q: "Can you carry roof boxes?", a: "Some vehicles support roof boxes on private charters only. For shared shuttles, we use interior cargo space configured for bags and ski gear." },
    ],
  },
  {
    category: "Pricing, Payments & Receipts",
    items: [
      { q: "How is pricing shown?", a: "Pricing is per seat. Your total reflects seats × fare, plus applicable taxes. We’ll show the full amount before you confirm." },
      { q: "What payment methods do you accept?", a: "Major cards are accepted online. For group or corporate bookings, contact us for invoicing options." },
      { q: "Do I get a receipt?", a: "Yes. We email a confirmation and receipt immediately after booking." },
    ],
  },
  {
    category: "Changes, Cancellations & Refunds",
    items: [
      { q: "Can I change my booking?", a: "Yes—subject to seat availability. Contact us and we’ll move you to another departure if possible." },
      { q: "What’s your cancellation policy?", a: "Cancel up to 24 hours before departure for a full refund. Within 24 hours, fares are non-refundable. If we cancel a departure, you’ll get a full refund or a free rebook." },
      { q: "What happens if a run is canceled due to weather/road closures?", a: "Safety first. If a run can’t operate, we’ll notify you and issue a full refund or rebook you at no charge." },
    ],
  },
  {
    category: "Accessibility & Special Requests",
    items: [
      { q: "Do you accommodate accessibility needs?", a: "We do our best—please contact us before booking so we can confirm vehicle configuration and assistance." },
      { q: "Can you do door-to-door service?", a: "Some routes support door-to-door or near-door pickups. Tell us your address during booking and we’ll confirm the closest pickup option." },
    ],
  },
  {
    category: "Contact & Support",
    items: [
      { q: "How do I reach you?", a: "Email info@karrentals.ca or call +1 (250) 575-6041. For day-of-travel changes, calling is fastest." },
      { q: "After hours?", a: "If your ride is today or in progress, call us. Otherwise, email and we’ll reply during business hours (typically same-day)." },
    ],
  },
];

/** One-open-at-a-time per category + true masonry columns (no neighbor stretch) */
function FAQSection({ category, items }) {
  const [openIndex, setOpenIndex] = useState(null);

  return (
    <div className="space-y-4">
      <h2 className="text-xl font-semibold">{category}</h2>

      {/* Masonry columns */}
      <div className="columns-1 md:columns-2 gap-4">
        {items.map((item, i) => {
          const isOpen = openIndex === i;
          const contentId = `${category.replace(/\s+/g, "-").toLowerCase()}-${i}`;

          // Vendor-prefixed break-inside guards
          const antiBreak = {
            breakInside: "avoid",
            WebkitColumnBreakInside: "avoid",
            MozColumnBreakInside: "avoid",
            pageBreakInside: "avoid",
            display: "inline-block",   // ensures item respects column flow
            width: "100%",             // so card spans full column width
          };

          return (
            <div key={item.q} className="mb-4" style={antiBreak}>
              <div className="rounded-xl border border-white/10 bg-zinc-900/40">
                <button
                  onClick={() => setOpenIndex(isOpen ? null : i)}
                  className="w-full flex items-center justify-between gap-4 px-4 py-3 text-left"
                  aria-expanded={isOpen}
                  aria-controls={contentId}
                >
                  <span className="font-medium">{item.q}</span>
                  <ChevronDownIcon
                    className={`h-5 w-5 shrink-0 transition-transform ${isOpen ? "rotate-180" : ""}`}
                  />
                </button>
                {isOpen && (
                  <div id={contentId} className="px-4 pb-4 pt-1 text-sm text-gray-300 leading-relaxed">
                    {item.a}
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

export default function FAQPage() {
  return (
    <Layout>
      <section className="container mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Hero */}
        <div className="relative overflow-hidden rounded-2xl">
          <div
            className="absolute inset-0 -z-10 bg-cover bg-center"
            style={{ backgroundImage: 'url("/images/hero/faq.jpg")' }}
            aria-hidden="true"
          />
          <div className="absolute inset-0 -z-10 bg-black/60" />
          <div className="px-6 sm:px-10 py-12 sm:py-16">
            <div className="inline-flex items-center gap-2 rounded-xl border border-white/10 bg-white/5 px-3 py-1 text-xs">
              <QuestionMarkCircleIcon className="h-4 w-4" />
              Frequently Asked Questions
            </div>
            <h1 className="mt-3 text-3xl sm:text-4xl font-semibold tracking-tight">
              KAR Shuttle — Okanagan & Interior BC (Per-Seat)
            </h1>
            <p className="mt-3 max-w-3xl text-sm sm:text-base text-gray-300">
              Everything you need to know about our shared ski shuttles and how to ride with us.
            </p>
          </div>
        </div>

        {/* Categories */}
        <div className="mt-8 space-y-8">
          {faqs.map((section) => (
            <FAQSection key={section.category} category={section.category} items={section.items} />
          ))}
        </div>

        {/* Footer note */}
        <div className="mt-10 rounded-2xl border border-white/10 bg-zinc-900/40 p-6 text-sm text-gray-300">
          Routes and schedules can vary by season and demand. If you don’t see the departure you need,
          email <a className="underline hover:no-underline" href="mailto:info@karrentals.ca">info@karrentals.ca</a> — we may be able to add capacity.
        </div>
      </section>
    </Layout>
  );
}
